import { useCallback } from 'react';

import { useAdminUserController_blockUser } from '@api-client/generated/AdminUserController/blockUser';
import { useAdminUserController_getUsers } from '@api-client/generated/AdminUserController/getUsers';
import { useAdminUserController_unblockUser } from '@api-client/generated/AdminUserController/unblockUser';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Blocking a backoffice member, over the generated client.
 *
 * What is left here is the part the generator does not express: the two roster lists share one
 * invalidation, and block and unblock are one instruction to this screen rather than two endpoints.
 * The error codes below are the backend's contract, so they are named once here rather than spelled
 * as string literals wherever they are compared.
 */

/** The backend refuses a block that would leave no unblocked backoffice admin. */
export const LAST_ADMIN_ERROR = 'last_admin';

/** The backend refuses an admin blocking their own account. */
export const CANNOT_BLOCK_SELF_ERROR = 'cannot_block_self';

/** Raised when the identity provider could not be reached; nothing was changed. */
export const IDENTITY_PROVIDER_UNAVAILABLE = 'identity_provider_unavailable';

/**
 * The key `useApiClient` builds for the roster is `[method, params]`, so its first element is the
 * operation id and stays the same whatever `includeBlocked` is. That is what the invalidation below
 * matches on.
 */
const MEMBERS_QUERY_ROOT = 'AdminUserController_getUsers';

/**
 * The backoffice roster.
 *
 * `includeBlocked` travels in the query key, so toggling it refetches rather than filtering a cached
 * list — the server decides who is visible, and it also recomputes `canBlock`, which depends on a
 * count across the whole table rather than on the rows being rendered.
 */
export const useMembers = (includeBlocked: boolean) =>
  useAdminUserController_getUsers({ params: { includeBlocked } });

/**
 * Block and unblock, sharing one invalidation.
 *
 * Invalidation is by query-key *root*, not by the exact key: there are two cached lists (blocked
 * shown and hidden) and a block invalidates both — the row leaves one and appears in the other.
 */
export const useBlockMember = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: block, isPending: isBlockPending } =
    useAdminUserController_blockUser();
  const { mutateAsync: unblock, isPending: isUnblockPending } =
    useAdminUserController_unblockUser();

  const invalidateMembers = useCallback(
    () =>
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === MEMBERS_QUERY_ROOT,
      }),
    [queryClient]
  );

  const setBlocked = useCallback(
    (userId: string, blocked: boolean, reason?: string) =>
      (blocked
        ? block({
            parameter: { id: userId },
            // Absent rather than empty: the reason is optional, and a blank string would be stored
            // as one and shown back to whoever reads why the member was blocked.
            requestBody: { reason: reason || undefined },
          })
        : unblock({ parameter: { id: userId } })
      ).finally(() => {
        void invalidateMembers();
      }),
    [block, unblock, invalidateMembers]
  );

  return { setBlocked, isBlocking: isBlockPending || isUnblockPending };
};
